import {useIntl} from 'react-intl'
import useMultiSite from '../../hooks/use-multi-site'
import {REGEX} from '../../constants'
import {useEffect, useState} from 'react'
import {useXSSProtection} from '../../hooks/use-xss-protection'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useToast} from '../../hooks/use-toast'

/**
 * Custom hook to manage the fields and validation for a newsletter subscription modal form.
 *
 * This hook provides a set of form fields with associated validation rules and error handling
 * for a newsletter subscription modal. It integrates with React Hook Form for form state management
 * and validation, and uses internationalization for field labels and error messages.
 *
 * @param {Object} params - Parameters for configuring the form fields.
 * @param {Object} params.form - The form object from React Hook Form.
 * @param {Object} params.form.control - Control object for managing form state.
 * @param {Object} params.form.formState - Object containing form state, including errors.
 * @param {Object} params.form.formState.errors - Object containing validation errors for form fields.
 * @param {Function} params.form.watch - Function to watch specific form field values.
 * @param {Function} params.form.setValue - Function to set the value of a specific form field.
 * @param {string} [params.prefix=''] - Optional prefix to prepend to form field names.
 * @returns {Object} An object containing form field configurations, including name, label, type, validation rules, and error state.
 */
export default function useNewsletterSubscriptionModalFields({
    form: {
        control,
        formState: {errors},
        watch,
        setValue,
        clearErrors
    },
    prefix = ''
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const countryCode = watch(`${prefix}countryCode`)
    const phone = watch(`${prefix}phone`)
    const {shipToCountry} = useShipToCountry()

    // Set the default phone country code based on the selected shipping country.
    const defaultCountryCode = locale?.id.split('-')[1]
    const finalCountryCode = shipToCountry?.id ? shipToCountry.id : defaultCountryCode

    const [defaultPhoneCountry, setDefaultPhoneCountry] = useState(finalCountryCode)

    // Sync phone country code on empty phone field
    useEffect(() => {
        if (phone === '') {
            setDefaultPhoneCountry(countryCode)
        }
    }, [countryCode])

    const {validateField} = useXSSProtection()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_newsletter_subscription_modal_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                validate: (value) =>
                    validateField(toast, value, 'newsletter-subscription-modal-from', 'firstname')
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_newsletter_subscription_modal_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                validate: (value) =>
                    validateField(toast, value, 'newsletter-subscription-modal-from', 'lastname')
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_newsletter_subscription_modal_fields.label.email'
            }),
            type: 'email',
            autoComplete: 'email',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'use_newsletter_subscription_modal_fields.required_email'
                }),
                pattern: {
                    value: REGEX.EMAIL,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address.',
                        id: 'use_newsletter_subscription_modal_fields.invalid_email'
                    })
                },
                validate: (value) => validateField(toast, value, 'registration-form', 'email')
            },
            error: errors[`${prefix}email`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_newsletter_subscription_modal_fields.label.phone'
            }),
            defaultValue: '',
            defaultCountry: defaultPhoneCountry,
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_newsletter_subscription_modal_fields.error.please_enter_phone_number'
                }),
                validate: (value) => validateField(toast, value, 'address-form', 'phone')
            },
            error: errors[`${prefix}phone`],
            control,
            setValue,
            clearErrors
        }
    }

    return fields
}
